import React, { memo } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PublicOffOutlinedIcon from '@mui/icons-material/PublicOffOutlined';
import { Box, Button, Text, useTheme } from '@ublend-npm/aulaui-core';
import { HOME_WIDGET_FONT_FAMILY } from '../homeWidgetTypography';
import { getSpaceWidgetColors } from './spaceWidgetColors';
import {
  SPACE_CARD_BORDER_RADIUS,
  SPACE_CARD_HEIGHT,
  SPACE_CARD_INNER_HEIGHT,
  SPACE_CARD_INNER_WIDTH,
  SPACE_CARD_PADDING_X,
  SPACE_CARD_PADDING_Y,
  SPACE_CARD_WIDTH,
  SPACE_WIDGET_ERROR_API_MESSAGE,
  SPACE_WIDGET_ERROR_BUTTON_ROW_HEIGHT,
  SPACE_WIDGET_ERROR_CONTENT_GAP,
  SPACE_WIDGET_ERROR_ICON_SIZE,
  SPACE_WIDGET_ERROR_INNER_PADDING_Y,
  SPACE_WIDGET_ERROR_MESSAGE_FONT_SIZE,
  SPACE_WIDGET_ERROR_MESSAGE_FONT_WEIGHT,
  SPACE_WIDGET_ERROR_MESSAGE_HEIGHT,
  SPACE_WIDGET_ERROR_MESSAGE_LETTER_SPACING,
  SPACE_WIDGET_ERROR_MESSAGE_LINE_HEIGHT,
  SPACE_WIDGET_ERROR_NETWORK_MESSAGE,
  SPACE_WIDGET_ERROR_RETRY_FONT_SIZE,
  SPACE_WIDGET_ERROR_RETRY_FONT_WEIGHT,
  SPACE_WIDGET_ERROR_RETRY_HEIGHT,
  SPACE_WIDGET_ERROR_RETRY_LABEL,
  SPACE_WIDGET_ERROR_RETRY_LETTER_SPACING,
  SPACE_WIDGET_ERROR_RETRY_LINE_HEIGHT,
  SPACE_WIDGET_ERROR_RETRY_PADDING,
} from './spaceWidgetConstants';

export type SpaceWidgetErrorVariant = 'network' | 'api';

type SpaceWidgetErrorProps = {
  variant?: SpaceWidgetErrorVariant;
  onRetry: () => void;
  isRetrying?: boolean;
};

const getErrorMessage = (variant: SpaceWidgetErrorVariant) =>
  variant === 'network'
    ? SPACE_WIDGET_ERROR_NETWORK_MESSAGE
    : SPACE_WIDGET_ERROR_API_MESSAGE;

const SpaceWidgetError = ({
  variant = 'api',
  onRetry,
  isRetrying = false,
}: SpaceWidgetErrorProps) => {
  const { palette, shape } = useTheme();
  const colors = getSpaceWidgetColors(palette);
  const message = getErrorMessage(variant);
  const Icon = variant === 'network' ? PublicOffOutlinedIcon : InfoOutlinedIcon;

  return (
    <Box
      component="article"
      role="alert"
      aria-live="assertive"
      aria-busy={isRetrying}
      aria-label={`${message}. ${SPACE_WIDGET_ERROR_RETRY_LABEL}`}
      sx={{
        width: SPACE_CARD_WIDTH,
        minWidth: SPACE_CARD_WIDTH,
        maxWidth: SPACE_CARD_WIDTH,
        height: SPACE_CARD_HEIGHT,
        flexShrink: 0,
        borderRadius: SPACE_CARD_BORDER_RADIUS,
        bgcolor: colors.cardBg,
        color: colors.cardText,
        fontFamily: HOME_WIDGET_FONT_FAMILY,
        boxSizing: 'border-box',
        boxShadow: colors.cardShadow,
        scrollSnapAlign: 'start',
        py: SPACE_CARD_PADDING_Y,
        px: SPACE_CARD_PADDING_X,
      }}
    >
      <Box
        sx={{
          width: SPACE_CARD_INNER_WIDTH,
          height: SPACE_CARD_INNER_HEIGHT,
          py: SPACE_WIDGET_ERROR_INNER_PADDING_Y,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: SPACE_WIDGET_ERROR_CONTENT_GAP,
          boxSizing: 'border-box',
        }}
      >
        <Icon
          aria-hidden
          sx={{
            fontSize: SPACE_WIDGET_ERROR_ICON_SIZE,
            color: colors.errorIcon,
          }}
        />
        <Text
          sx={{
            m: 0,
            width: SPACE_CARD_INNER_WIDTH,
            height: SPACE_WIDGET_ERROR_MESSAGE_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: HOME_WIDGET_FONT_FAMILY,
            fontSize: SPACE_WIDGET_ERROR_MESSAGE_FONT_SIZE,
            fontWeight: SPACE_WIDGET_ERROR_MESSAGE_FONT_WEIGHT,
            lineHeight: SPACE_WIDGET_ERROR_MESSAGE_LINE_HEIGHT,
            letterSpacing: SPACE_WIDGET_ERROR_MESSAGE_LETTER_SPACING,
            color: colors.errorMessage,
            textAlign: 'center',
          }}
        >
          {message}
        </Text>
        <Box
          sx={{
            width: SPACE_CARD_INNER_WIDTH,
            height: SPACE_WIDGET_ERROR_BUTTON_ROW_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Button
            kind="primary"
            disabled={isRetrying}
            onClick={onRetry}
            aria-label={SPACE_WIDGET_ERROR_RETRY_LABEL}
            sx={{
              height: SPACE_WIDGET_ERROR_RETRY_HEIGHT,
              minHeight: SPACE_WIDGET_ERROR_RETRY_HEIGHT,
              p: SPACE_WIDGET_ERROR_RETRY_PADDING,
              borderRadius: `${shape.borderRadius}px`,
              bgcolor: colors.errorRetryBg,
              color: colors.errorRetryText,
              textTransform: 'none',
              fontFamily: HOME_WIDGET_FONT_FAMILY,
              fontWeight: SPACE_WIDGET_ERROR_RETRY_FONT_WEIGHT,
              fontSize: SPACE_WIDGET_ERROR_RETRY_FONT_SIZE,
              lineHeight: SPACE_WIDGET_ERROR_RETRY_LINE_HEIGHT,
              letterSpacing: SPACE_WIDGET_ERROR_RETRY_LETTER_SPACING,
              boxShadow: 'none',
              '&:hover': {
                bgcolor: colors.errorRetryHoverBg,
                boxShadow: 'none',
              },
              '&.Mui-disabled': {
                opacity: 0.6,
                color: colors.errorRetryText,
                bgcolor: colors.errorRetryBg,
              },
            }}
          >
            {SPACE_WIDGET_ERROR_RETRY_LABEL}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default memo(SpaceWidgetError);
